import { getValidToken } from './keycloak.service';

// Lazily-initialized singleton: anything in the app can subscribe at any
// time and only the first subscriber actually opens the socket. Re-opens
// with exponential backoff on close so transient drops are invisible to
// the UI. Listeners receive { type, data } envelopes parsed from JSON.

const PING_MS = 30_000;
const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

const listeners = new Map(); // eventType -> Set<handler>
const lifecycleListeners = { connect: new Set(), disconnect: new Set() };
// Station ids the UI wants OCPP frames for. Re-sent on every (re)connect so a
// dropped socket transparently resumes the same subscriptions.
const stationSubscriptions = new Set();

let socket = null;
let intentionalClose = false;
let connectingPromise = null;
let backoffMs = BACKOFF_INITIAL_MS;
let pingHandle = null;

function buildUrl(token) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${proto}//${host}/ws/events?access_token=${encodeURIComponent(token)}`;
}

async function connectOnce() {
    const token = await getValidToken();
    if (!token) throw new Error('No token; cannot open events socket');
    return new Promise((resolve, reject) => {
        const ws = new WebSocket(buildUrl(token));

        ws.addEventListener('open', () => {
            socket = ws;
            backoffMs = BACKOFF_INITIAL_MS;
            startPing();
            // Resume any station subscriptions on the fresh socket.
            stationSubscriptions.forEach((id) =>
                sendRaw({ action: 'subscribe', stationId: id })
            );
            lifecycleListeners.connect.forEach((fn) => safeCall(fn));
            resolve();
        });

        ws.addEventListener('message', (evt) => {
            if (evt.data === 'pong') return;
            try {
                const frame = JSON.parse(evt.data);
                dispatch(frame.type, frame.data);
            } catch (err) {
                console.warn('Bad events frame', err);
            }
        });

        ws.addEventListener('close', () => {
            stopPing();
            socket = null;
            connectingPromise = null;
            lifecycleListeners.disconnect.forEach((fn) => safeCall(fn));
            if (!intentionalClose) scheduleReconnect();
        });

        ws.addEventListener('error', (err) => {
            // 'close' will follow; surface the failure to the original caller
            // (subsequent reconnects don't have a caller to reject).
            if (!socket) reject(err);
        });
    });
}

function scheduleReconnect() {
    const delay = backoffMs;
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
    setTimeout(() => {
        if (intentionalClose) return;
        connectingPromise = connectOnce().catch(() => {
            // connectOnce already arranged the next 'close' to schedule again.
        });
    }, delay);
}

function startPing() {
    stopPing();
    pingHandle = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            try {
                socket.send('ping');
            } catch (err) {
                // ignore; close handler will recover
            }
        }
    }, PING_MS);
}

function stopPing() {
    if (pingHandle) {
        clearInterval(pingHandle);
        pingHandle = null;
    }
}

function dispatch(type, data) {
    const set = listeners.get(type);
    if (!set) return;
    set.forEach((fn) => safeCall(() => fn(data)));
}

function safeCall(fn) {
    try {
        fn();
    } catch (err) {
        console.warn('Events listener threw', err);
    }
}

export function connect() {
    intentionalClose = false;
    if (socket && socket.readyState === WebSocket.OPEN) return Promise.resolve();
    if (connectingPromise) return connectingPromise;
    connectingPromise = connectOnce();
    return connectingPromise;
}

export function disconnect() {
    intentionalClose = true;
    stopPing();
    if (socket) {
        try { socket.close(); } catch (err) { /* ignore */ }
        socket = null;
    }
    connectingPromise = null;
}

function sendRaw(obj) {
    if (socket && socket.readyState === WebSocket.OPEN) {
        try {
            socket.send(JSON.stringify(obj));
        } catch (err) {
            // open handler re-sends on reconnect
        }
    }
}

export function subscribeStation(stationId) {
    if (!stationId) return;
    stationSubscriptions.add(stationId);
    sendRaw({ action: 'subscribe', stationId });
}

export function unsubscribeStation(stationId) {
    if (!stationId) return;
    stationSubscriptions.delete(stationId);
    sendRaw({ action: 'unsubscribe', stationId });
}

export function on(type, handler) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(handler);
}

export function off(type, handler) {
    const set = listeners.get(type);
    if (set) set.delete(handler);
}

export function onConnect(handler) {
    lifecycleListeners.connect.add(handler);
}

export function offConnect(handler) {
    lifecycleListeners.connect.delete(handler);
}
